import React, { useState, useEffect } from 'react'
import { formatCurrency } from '../utils/formatters'
import { api } from '../services/authService'
import toast from 'react-hot-toast'
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  WrenchScrewdriverIcon,
  TruckIcon,
  IdentificationIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'

const VehicleDetailModal = ({ vehicle, onClose, onEdit, onCreateContract }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [insurances, setInsurances] = useState([])
  const [loadingInsurances, setLoadingInsurances] = useState(true)

  useEffect(() => {
    if (vehicle?.id) {
      loadInsurances()
    }
  }, [vehicle?.id])

  const loadInsurances = async () => {
    try {
      setLoadingInsurances(true)
      const response = await api.get(`/api/compliance/insurances?vehicleId=${vehicle.id}`)
      if (response.data.success) {
        setInsurances(response.data.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar seguros:', error)
      toast.error('Erro ao carregar seguros do veículo')
    } finally {
      setLoadingInsurances(false)
    }
  }

  if (!vehicle) return null

  const images = vehicle.images || []
  const hasImages = images.length > 0

  const nextImage = () => {
    if (hasImages) {
      setCurrentImageIndex((prev) => (prev + 1) % images.length)
    }
  }

  const prevImage = () => {
    if (hasImages) {
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)
    }
  }

  const getStatusBadge = (status) => {
    const badges = {
      available: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      rented: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      maintenance: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      inactive: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    }
    
    const labels = {
      available: 'Disponível',
      rented: 'Em Uso',
      maintenance: 'Manutenção',
      inactive: 'Inativo'
    }
    
    return (
      <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${badges[status]}`}>
        {labels[status]}
      </span>
    )
  }

  const getTypeIcon = (type) => {
    const icons = {
      excavator: '🏗️',
      truck: '🚛',
      tractor: '🚜',
      crane: '🏗️',
      loader: '🚧',
      bulldozer: '🚜'
    }
    return icons[type] || '🚛'
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-4xl max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-4">
            <div className="text-3xl">{getTypeIcon(vehicle.type)}</div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {vehicle.model}
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                {vehicle.brand} • {vehicle.year}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {getStatusBadge(vehicle.status)}
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row max-h-[calc(95vh-80px)] overflow-hidden">
          {/* Galeria de Imagens */}
          <div className="lg:w-1/2 bg-gray-100 dark:bg-gray-700">
            <div className="relative h-64 lg:h-full">
              {hasImages ? (
                <>
                  <img
                    src={`${import.meta.env.VITE_API_URL || ''}${images[currentImageIndex].url}`}
                    alt={`${vehicle.brand} ${vehicle.model}`}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Navegação das imagens */}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
                      >
                        <ChevronLeftIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
                      >
                        <ChevronRightIcon className="w-5 h-5" />
                      </button>
                      
                      {/* Indicador de posição */}
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                        {currentImageIndex + 1} de {images.length}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-8xl opacity-30 mb-4">{getTypeIcon(vehicle.type)}</div>
                    <p className="text-gray-500 dark:text-gray-400">Nenhuma imagem disponível</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Miniaturas */}
            {images.length > 1 && (
              <div className="p-4 bg-white dark:bg-gray-800">
                <div className="flex space-x-2 overflow-x-auto">
                  {images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                        index === currentImageIndex 
                          ? 'border-blue-500' 
                          : 'border-gray-200 dark:border-gray-600'
                      }`}
                    >
                      <img
                        src={`${import.meta.env.VITE_API_URL || ''}${image.url}`}
                        alt={`Miniatura ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Detalhes do Veículo */}
          <div className="lg:w-1/2 p-6 overflow-y-auto">
            <div className="space-y-6">
              {/* Informações Básicas */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <IdentificationIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Placa</p>
                    <p className="font-medium text-gray-900 dark:text-white">{vehicle.license_plate}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <CalendarDaysIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Ano</p>
                    <p className="font-medium text-gray-900 dark:text-white">{vehicle.year}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <CurrencyDollarIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Valor Mensal</p>
                    <p className="font-medium text-green-600">{formatCurrency(Number(vehicle.monthly_rate))}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <TruckIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Tipo</p>
                    <p className="font-medium text-gray-900 dark:text-white capitalize">{vehicle.type}</p>
                  </div>
                </div>
              </div>

              {/* Especificações Técnicas */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Especificações Técnicas
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  {vehicle.color && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Cor:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{vehicle.color}</span>
                    </div>
                  )}
                  
                  {vehicle.engine_power && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Potência do Motor:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{vehicle.engine_power}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Combustível:</span>
                    <span className="font-medium text-gray-900 dark:text-white capitalize">{vehicle.fuel_type}</span>
                  </div>
                </div>
              </div>

              {/* Seguros */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <ShieldCheckIcon className="w-5 h-5 mr-2" />
                  Seguros
                </h3>

                {loadingInsurances ? (
                  <p className="text-sm text-gray-600 dark:text-gray-400">Carregando seguros...</p>
                ) : insurances.length === 0 ? (
                  <p className="text-sm text-gray-600 dark:text-gray-400">Nenhum seguro cadastrado</p>
                ) : (
                  <div className="space-y-4">
                    {insurances.map((insurance) => (
                      <div
                        key={insurance.id}
                        className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {insurance.insurance_company}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Apólice: {insurance.policy_number}
                            </p>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            insurance.status === 'active'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          }`}>
                            {insurance.status === 'active' ? 'Ativo' : 'Expirado'}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {insurance.policy_type && (
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Tipo:</span>
                              <span className="ml-2 font-medium text-gray-900 dark:text-white">
                                {insurance.policy_type}
                              </span>
                            </div>
                          )}

                          {insurance.coverage_amount && (
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Cobertura:</span>
                              <span className="ml-2 font-medium text-green-600">
                                {formatCurrency(Number(insurance.coverage_amount))}
                              </span>
                            </div>
                          )}

                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Início:</span>
                            <span className="ml-2 font-medium text-gray-900 dark:text-white">
                              {new Date(insurance.start_date).toLocaleDateString('pt-BR')}
                            </span>
                          </div>

                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Vencimento:</span>
                            <span className="ml-2 font-medium text-gray-900 dark:text-white">
                              {new Date(insurance.end_date).toLocaleDateString('pt-BR')}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Informações de Sistema */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Informações do Sistema
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">ID do Veículo:</span>
                    <span className="font-medium text-gray-900 dark:text-white">#{vehicle.id}</span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Data de Cadastro:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatDate(vehicle.created_at)}
                    </span>
                  </div>
                  
                  {vehicle.updated_at && vehicle.updated_at !== vehicle.created_at && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Última Atualização:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {formatDate(vehicle.updated_at)}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Total de Imagens:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{images.length}</span>
                  </div>
                </div>
              </div>

              {/* Estatísticas (se disponível) */}
              {(vehicle.maintenance_score || vehicle.total_contracts || vehicle.total_revenue) && (
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Estatísticas
                  </h3>
                  <div className="grid grid-cols-1 gap-4">
                    {vehicle.maintenance_score && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Score de Manutenção:</span>
                        <span className={`font-medium ${
                          vehicle.maintenance_score >= 80 ? 'text-green-600' : 
                          vehicle.maintenance_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {vehicle.maintenance_score}/100
                        </span>
                      </div>
                    )}
                    
                    {vehicle.total_contracts && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Total de Contratos:</span>
                        <span className="font-medium text-gray-900 dark:text-white">{vehicle.total_contracts}</span>
                      </div>
                    )}
                    
                    {vehicle.total_revenue && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Receita Total:</span>
                        <span className="font-medium text-green-600">R$ {vehicle.total_revenue.toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Botões de Ação */}
              <div className="border-t pt-6">
                <div className="flex space-x-3">
                  {onEdit && (
                    <button
                      onClick={() => onEdit(vehicle)}
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Editar Veículo
                    </button>
                  )}
                  {onCreateContract && (
                    <button
                      onClick={() => onCreateContract(vehicle)}
                      className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Criar Contrato
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VehicleDetailModal